/**
 * "Frequently used" emoji (Slack, docs/design/slack-gap.md), per person and workspace, on the server so every device
 * agrees: reactions count automatically (adding one); emoji picked in the composer are reported with POST. The list is
 * ranked by frecency (core logic/frequentEmoji) and padded with Slack-like defaults.
 */
import { z } from "zod";

/** The emoji skin tone I picked (Slack's picker preference; "none" = the default yellow). */
export const SkinTone = z.enum(["none", "light", "medium-light", "medium", "medium-dark", "dark"]);
export type SkinTone = z.infer<typeof SkinTone>;

/**
 * GET /v1/w/:ws/emoji/frequent — my most used emoji, best first (at most 24), and my skin tone (absent from older
 * servers = "none").
 */
export const FrequentEmojiResponse = z.object({ emoji: z.array(z.string()), skinTone: SkinTone.optional() });
export type FrequentEmojiResponse = z.infer<typeof FrequentEmojiResponse>;

/** POST /v1/w/:ws/emoji/frequent — emoji I just used outside reactions (composer picker / :autocomplete:). → 204 */
export const RecordEmojiRequest = z.object({ emoji: z.array(z.string().min(1).max(64)).min(1).max(20) });
export type RecordEmojiRequest = z.infer<typeof RecordEmojiRequest>;

/** PUT /v1/w/:ws/emoji/skin-tone — my skin tone for emoji that have one (per person and workspace, like Slack). → 204 */
export const SetSkinToneRequest = z.object({ skinTone: SkinTone });
export type SetSkinToneRequest = z.infer<typeof SetSkinToneRequest>;
